'use client';

import React, { useState, useRef, useEffect } from 'react';
import AutoGrowingTextarea from '@/components/ui/AutoGrowingTextarea';
import GenerateButton from '@/components/ui/GenerateButton';

interface PasswordOptions {
    uppercase: boolean;
    lowercase: boolean;
    numbers: boolean;
    removeAmbiguous: boolean;
    useCustomSymbols: boolean;
    customSymbols: string;
    minLength: number;
    maxLength: number;
    count: number;
}

function GeneratePasswordsPage() {
    const [options, setOptions] = useState<PasswordOptions>({
        uppercase: true,
        lowercase: true,
        numbers: true,
        removeAmbiguous: false,
        useCustomSymbols: true,
        customSymbols: '!@#$%^&*()_+{}[]|:;<>,.?~',
        minLength: 8,
        maxLength: 16,
        count: 1,
    });

    const [generatedPasswords, setGeneratedPasswords] = useState<string>('');

    const getValueFromWithType = (type: string, value: string, checked: boolean) => {
        switch (type) {
            case 'number':
                return parseInt(value);
            case 'checkbox':
                return checked;
            default:
                return value;
        }
    }

    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = event.target;
        setOptions(prevOptions => ({
            ...prevOptions,
            [name]: getValueFromWithType(type, value, checked),
        }));
    };

    const generatePasswords = () => {
        if (options.maxLength < options.minLength) {
            return;
        }
        const charset = {
            uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
            lowercase: 'abcdefghijklmnopqrstuvwxyz',
            numbers: '0123456789',
            symbols: options.useCustomSymbols ? options.customSymbols : '',
        };

        const ambiguousChars = 'iIlL1oO0';

        let availableChars = '';
        if (options.uppercase) availableChars += charset.uppercase;
        if (options.lowercase) availableChars += charset.lowercase;
        if (options.numbers) availableChars += charset.numbers;
        if (options.useCustomSymbols) availableChars += charset.symbols;

        if (options.removeAmbiguous) {
            availableChars = availableChars.split('').filter(char => !ambiguousChars.includes(char)).join('');
        }

        const passwords: string[] = [];

        for (let i = 0; i < options.count; i++) {
            const length = Math.floor(Math.random() * (options.maxLength - options.minLength + 1)) + options.minLength;
            let password = '';
            for (let j = 0; j < length; j++) {
                password += availableChars.charAt(Math.floor(Math.random() * availableChars.length));
            }
            passwords.push(password);
        }

        setGeneratedPasswords(passwords.join('\n'));
    };

    return (
        <div className="w-full p-4">
            <div className='flex-grow flex justify-center mb-6'>
                <h1 className="text-2xl font-bold">Generate Passwords</h1>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - Configuration */}
                <div className="w-full md:w-1/2">
                    <div className="form-control mb-4">
                        <h2 className="text-xl font-semibold mb-2">Options</h2>
                        <label className="label cursor-pointer justify-start">
                            <input type="checkbox" className="checkbox mr-2 checkbox-sm" name="uppercase" checked={options.uppercase} onChange={handleOptionChange} />
                            <span className="label-text">Uppercase</span>
                        </label>
                        <label className="label cursor-pointer justify-start">
                            <input type="checkbox" className="checkbox mr-2 checkbox-sm" name="lowercase" checked={options.lowercase} onChange={handleOptionChange} />
                            <span className="label-text">Lowercase</span>
                        </label>
                        <label className="label cursor-pointer justify-start">
                            <input type="checkbox" className="checkbox mr-2 checkbox-sm" name="numbers" checked={options.numbers} onChange={handleOptionChange} />
                            <span className="label-text">Numbers</span>
                        </label>
                        <label className="label cursor-pointer justify-start">
                            <input type="checkbox" className="checkbox mr-2 checkbox-sm" name="removeAmbiguous" checked={options.removeAmbiguous} onChange={handleOptionChange} />
                            <span className="label-text">Remove Ambiguous Characters</span>
                        </label>
                        <label className="label cursor-pointer justify-start">
                            <input type="checkbox" className="checkbox mr-2 checkbox-sm" name="useCustomSymbols" checked={options.useCustomSymbols} onChange={handleOptionChange} />
                            <span className="label-text">Use Custom Symbols</span>
                        </label>
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Custom Symbols</span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered w-full input-sm"
                            name="customSymbols"
                            value={options.customSymbols}
                            onChange={handleOptionChange}
                            disabled={!options.useCustomSymbols}
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Minimum Length (1-256)</span>
                        </label>
                        <input
                            type="number"
                            className={`input input-bordered w-full input-sm ${options.maxLength < options.minLength || options.minLength < 1 || options.minLength > 256 ? 'input-error' : ''}`}
                            name="minLength"
                            min="1"
                            max="256"
                            value={options.minLength}
                            onChange={handleOptionChange}
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Maximum Length (1-256)</span>
                        </label>
                        <input
                            type="number"
                            className={`input input-bordered w-full input-sm ${options.maxLength < options.minLength || options.maxLength < 1 || options.maxLength > 256 ? 'input-error' : ''}`}
                            name="maxLength"
                            min="1"
                            max="256"
                            value={options.maxLength}
                            onChange={handleOptionChange}
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Number of Passwords (1-100)</span>
                        </label>
                        <input
                            type="number"
                            className={`input input-bordered w-full input-sm ${options.count < 1 || options.count > 100 ? 'input-error' : ''}`}
                            name="count"
                            min="1"
                            max="100"
                            value={options.count}
                            onChange={handleOptionChange}
                        />
                    </div>
                    <GenerateButton generatePasswords={generatePasswords} />
                </div>

                {/* Right Column - Generated Passwords */}
                <div className="w-full md:w-1/2">
                    <h2 className="text-xl font-semibold mb-2">Generated Passwords</h2>
                    <AutoGrowingTextarea
                        value={generatedPasswords}
                        minHeight={128}
                        maxHeight={512}
                    />
                </div>
            </div>
        </div>
    );
}

export default GeneratePasswordsPage;